import type { Database } from "@/integrations/supabase/types";

export type CardRow = Database["public"]["Tables"]["cards"]["Row"];

export interface SegmentChip {
  id: string;
  label: string;
  predicate: (card: CardRow) => boolean;
}

export interface SegmentFaq {
  question: string;
  answer: string;
}

export interface SegmentSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface SegmentPageConfig {
  /** URL path without leading slash */
  path: string;
  /** Key used by the "Beliebte Kreditkarten" module visibility settings */
  pageKey: string;
  seoTitle: string;
  seoDescription: string;
  h1: string;
  intro: string;
  /** Base filter — always applied on this page */
  baseFilter: (card: CardRow) => boolean;
  /** Word used in the result counter, e.g. "kostenlose Kreditkarten" */
  countNoun: string;
  chips: SegmentChip[];
  sections: SegmentSection[];
  faqs: SegmentFaq[];
}

const textOf = (card: CardRow): string =>
  [
    card.name,
    card.issuer,
    card.description,
    card.excerpt,
    (card.benefits ?? []).join(" "),
    (card.selling_points ?? []).join(" "),
    (card.pros ?? []).join(" "),
    card.perks ? JSON.stringify(card.perks) : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/**
 * Only predicates backed by fields that actually carry data across the card
 * feed are used here. Fields that are empty for every card today
 * (Fremdwährungsgebühr, ATM-Texte, Wallet-Flags, Kreditrahmen,
 * Mindesteinkommen, Cashback) must not become filter chips — they would
 * always return zero results.
 */
export const hasNoAnnualFee = (card: CardRow) => (card.annual_fee ?? 0) === 0;
export const isPrepaid = (card: CardRow) =>
  card.is_prepaid === true || String(card.card_type ?? "").toLowerCase() === "prepaid";
export const withoutAccount = (card: CardRow) => card.current_account_included !== true;
export const withAccount = (card: CardRow) => card.current_account_included === true;
export const hasTravelInsurance = (card: CardRow) =>
  card.travel_insurance === true ||
  card.travel_health_insurance === true ||
  card.travel_cancellation_insurance === true ||
  card.travel_baggage_insurance === true ||
  card.travel_accident_insurance === true;
export const isNetwork = (network: string) => (card: CardRow) =>
  String(card.network ?? "").toLowerCase() === network;
export const noSchufaLikely = (card: CardRow) => isPrepaid(card) || textOf(card).includes("ohne schufa");
export const isRealCreditCard = (card: CardRow) => {
  const type = String(card.card_type ?? "").toLowerCase();
  // Some feed rows are typed "Kreditkarte" but are in fact debit cards; the
  // product name is the only reliable marker for those today.
  const label = `${card.name ?? ""} ${card.display_name ?? ""}`.toLowerCase();
  const isDebit =
    type.includes("debit") || label.includes("debit") || label.includes("debitkarte");
  return !isPrepaid(card) && !isDebit;
};

export const segmentPages: SegmentPageConfig[] = [
  {
    path: "kostenlose-kreditkarte",
    pageKey: "landing-kostenlose-kreditkarte",
    seoTitle: "Kostenlose Kreditkarte Vergleich 2026 – ohne Jahresgebühr",
    seoDescription:
      "Kostenlose Kreditkarten im Vergleich: ohne Jahresgebühr, ohne Fremdwährungsgebühr und ohne Girokonto. Alle Konditionen transparent gegenübergestellt.",
    h1: "Kostenlose Kreditkarten im Vergleich",
    intro:
      "Alle Kreditkarten ohne Jahresgebühr auf einen Blick. Filtern Sie nach Karten ohne neues Girokonto, nach echtem Kreditrahmen und nach dem Kartennetzwerk.",
    baseFilter: hasNoAnnualFee,
    countNoun: "kostenlose Kreditkarten",
    chips: [
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
      { id: "real-credit", label: "Echte Kreditkarte", predicate: isRealCreditCard },
      { id: "travel", label: "Mit Reiseversicherung", predicate: hasTravelInsurance },
      { id: "visa", label: "Visa", predicate: isNetwork("visa") },
      { id: "mastercard", label: "Mastercard", predicate: isNetwork("mastercard") },
      { id: "amex", label: "American Express", predicate: isNetwork("american_express") },
    ],
    sections: [
      {
        heading: "Was bedeutet „kostenlose Kreditkarte“ wirklich?",
        paragraphs: [
          "Kostenlos bezieht sich in fast allen Werbeaussagen ausschließlich auf die Jahresgebühr. Ob eine Karte im Alltag tatsächlich nichts kostet, entscheiden drei weitere Positionen: das Entgelt für Zahlungen in Fremdwährung, die Gebühr für Bargeldabhebungen und mögliche Kosten für ein verpflichtendes Girokonto.",
          "In unserem Vergleich sehen Sie diese Positionen einzeln, nicht als Werbeversprechen. So erkennen Sie sofort, ob eine Karte nur beim Jahresbeitrag oder auch im Einsatz kostenfrei bleibt.",
        ],
        bullets: [
          "Jahresgebühr: dauerhaft 0 € statt nur im ersten Jahr",
          "Fremdwährungsgebühr: typischerweise 1,75 % bis 2,00 %, bei guten Karten 0 %",
          "Bargeldabhebung: häufig 2 % vom Betrag oder ein Mindestentgelt",
          "Girokontopflicht: manche Karten gibt es nur im Paket mit einem Konto",
        ],
      },
      {
        heading: "Achtung bei Teilzahlung",
        paragraphs: [
          "Viele gebührenfreie Kreditkarten sind ab Werk auf Teilzahlung (Revolving) eingestellt. Der offene Betrag wird dann monatlich nur anteilig ausgeglichen und mit einem effektiven Jahreszins verzinst, der häufig deutlich über 20 % liegt. Wer die Karte auf Vollzahlung umstellt, zahlt keine Zinsen.",
        ],
      },
    ],
    faqs: [
      {
        question: "Gibt es wirklich dauerhaft kostenlose Kreditkarten?",
        answer:
          "Ja. Mehrere Anbieter verzichten dauerhaft auf die Jahresgebühr, nicht nur im ersten Jahr. Prüfen Sie zusätzlich die Gebühren für Fremdwährung und Bargeld, denn diese fallen bei vielen kostenlosen Karten trotzdem an.",
      },
      {
        question: "Brauche ich für eine kostenlose Kreditkarte ein neues Girokonto?",
        answer:
          "Nicht zwingend. Ein Teil der Karten wird unabhängig von einem Girokonto ausgegeben und per Lastschrift von Ihrem bestehenden Konto abgerechnet. Nutzen Sie dafür den Filter „Ohne Girokonto“.",
      },
      {
        question: "Ist eine kostenlose Kreditkarte auch für Mietwagen und Hotels geeignet?",
        answer:
          "Nur, wenn es sich um eine echte Kreditkarte mit Kreditrahmen handelt. Prepaid- und Debitkarten werden für Kautionen häufig abgelehnt. Der Filter „Echte Kreditkarte“ zeigt Ihnen passende Karten.",
      },
      {
        question: "Fallen bei kostenlosen Kreditkarten Zinsen an?",
        answer:
          "Nur wenn Sie Teilzahlung nutzen. Bei vollständigem monatlichem Ausgleich der Abrechnung entstehen keine Sollzinsen.",
      },
    ],
  },
  {
    path: "prepaid-kreditkarte",
    pageKey: "landing-prepaid-kreditkarte",
    seoTitle: "Prepaid Kreditkarte Vergleich 2026 – ohne SCHUFA & auf Guthabenbasis",
    seoDescription:
      "Prepaid Kreditkarten im Vergleich: auf Guthabenbasis, ohne SCHUFA-Prüfung, ohne Girokonto und sofort einsetzbar. Alle Gebühren transparent.",
    h1: "Prepaid Kreditkarten im Vergleich",
    intro:
      "Prepaid-Karten funktionieren auf Guthabenbasis: Sie laden die Karte auf und geben nur aus, was vorhanden ist. Damit gibt es keinen Kreditrahmen, keine Zinsen und in der Regel keine SCHUFA-Prüfung.",
    baseFilter: isPrepaid,
    countNoun: "Prepaid-Kreditkarten",
    chips: [
      { id: "free", label: "Ohne Jahresgebühr", predicate: hasNoAnnualFee },
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
      { id: "mastercard", label: "Mastercard", predicate: isNetwork("mastercard") },
    ],
    sections: [
      {
        heading: "So funktioniert eine Prepaid-Kreditkarte",
        paragraphs: [
          "Eine Prepaid-Kreditkarte ist streng genommen keine Kreditkarte, weil kein Kredit gewährt wird. Sie überweisen Guthaben auf das Kartenkonto und können anschließend bis zur Höhe dieses Guthabens bezahlen. Eine Überziehung ist ausgeschlossen.",
          "Weil kein Kreditrisiko entsteht, verzichten die Anbieter meist auf eine Bonitätsprüfung. Das macht die Karte für Menschen mit negativer SCHUFA, für Studierende und für Jugendliche interessant.",
        ],
        bullets: [
          "Kein Kreditrahmen, keine Zinsen, keine Teilzahlung",
          "Volle Kostenkontrolle, ideal als Zweitkarte für Online-Käufe",
          "Akzeptanz weltweit wie bei Visa oder Mastercard",
        ],
      },
      {
        heading: "Wo Prepaid-Karten an Grenzen stoßen",
        paragraphs: [
          "Bei Mietwagen-Kautionen, Hotelreservierungen und einigen Buchungsportalen werden Prepaid-Karten regelmäßig abgelehnt, weil dort ein Verfügungsrahmen blockiert werden muss. Wer die Karte für Reisen einsetzen möchte, sollte diesen Punkt vorab prüfen.",
        ],
      },
    ],
    faqs: [
      {
        question: "Bekomme ich eine Prepaid-Kreditkarte ohne SCHUFA-Abfrage?",
        answer:
          "In den meisten Fällen ja. Da kein Kredit vergeben wird, verzichten viele Anbieter auf eine Bonitätsprüfung. Eine Identitätsprüfung ist gesetzlich trotzdem vorgeschrieben.",
      },
      {
        question: "Kann ich mit einer Prepaid-Karte einen Mietwagen buchen?",
        answer:
          "Meist nicht. Autovermietungen verlangen in der Regel eine echte Kreditkarte mit Kreditrahmen, um die Kaution zu blockieren.",
      },
      {
        question: "Was kostet eine Prepaid-Kreditkarte?",
        answer:
          "Es gibt Karten ohne Jahresgebühr. Achten Sie zusätzlich auf Aufladeentgelte, Gebühren für Bargeldabhebungen und das Fremdwährungsentgelt.",
      },
      {
        question: "Ab welchem Alter gibt es eine Prepaid-Karte?",
        answer:
          "Einige Anbieter geben Prepaid-Karten bereits an Jugendliche unter 18 Jahren aus, meist mit Zustimmung der Erziehungsberechtigten.",
      },
    ],
  },
  {
    path: "kreditkarte-ohne-schufa",
    pageKey: "landing-kreditkarte-ohne-schufa",
    seoTitle: "Kreditkarte ohne SCHUFA 2026 – welche Karten es wirklich gibt",
    seoDescription:
      "Kreditkarte ohne SCHUFA: Welche Karten ohne Bonitätsprüfung vergeben werden, wo der Unterschied zwischen Prepaid, Debit und echter Kreditkarte liegt.",
    h1: "Kreditkarte ohne SCHUFA",
    intro:
      "Karten, die ohne klassische Bonitätsprüfung ausgegeben werden. Wichtig zu wissen: Fast immer handelt es sich dabei um Prepaid- oder Debitkarten, nicht um eine Kreditkarte mit echtem Kreditrahmen.",
    baseFilter: noSchufaLikely,
    countNoun: "Karten ohne klassische Bonitätsprüfung",
    chips: [
      { id: "prepaid", label: "Prepaid", predicate: isPrepaid },
      { id: "free", label: "Ohne Jahresgebühr", predicate: hasNoAnnualFee },
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
    ],
    sections: [
      {
        heading: "Kann ich wirklich eine Kreditkarte ohne SCHUFA bekommen?",
        paragraphs: [
          "Ehrliche Antwort: eine echte Kreditkarte mit Kreditrahmen praktisch nicht. Sobald eine Bank Ihnen Geld vorstreckt, prüft sie Ihre Bonität — das schreibt der Gesetzgeber vor. Ohne SCHUFA erhalten Sie in Deutschland deshalb vor allem Karten, bei denen kein Kredit im Spiel ist.",
        ],
        bullets: [
          "Prepaid-Karte: Guthabenbasis, keine Bonitätsprüfung, weltweit einsetzbar",
          "Debitkarte: Abbuchung direkt vom Konto, teils ohne SCHUFA-Abfrage",
          "Echte Kreditkarte: Kreditrahmen, immer mit Bonitätsprüfung",
        ],
      },
      {
        heading: "Worauf Sie bei Angeboten achten sollten",
        paragraphs: [
          "Anbieter, die eine „Kreditkarte ohne SCHUFA“ mit hohem Verfügungsrahmen gegen Vorkosten versprechen, sind unseriös. Seriöse Karten ohne SCHUFA-Abfrage verlangen niemals eine Vorabgebühr für die Zusage. Eine Identitätsprüfung per Video- oder Post-Ident ist dagegen normal und gesetzlich vorgeschrieben.",
        ],
      },
    ],
    faqs: [
      {
        question: "Gibt es eine echte Kreditkarte ohne SCHUFA?",
        answer:
          "In der Praxis nein. Karten mit echtem Kreditrahmen setzen eine Bonitätsprüfung voraus. Ohne SCHUFA erhalten Sie Prepaid- oder Debitkarten.",
      },
      {
        question: "Wird bei der Beantragung ein SCHUFA-Eintrag erzeugt?",
        answer:
          "Bei Prepaid-Karten in der Regel nicht. Bei Karten mit Kreditrahmen meldet die Bank die Kartenausgabe an die SCHUFA — das ist ein neutrales Merkmal und verschlechtert Ihren Score nicht automatisch.",
      },
      {
        question: "Kann ich eine Karte trotz negativer SCHUFA nutzen?",
        answer:
          "Ja. Eine Prepaid-Kreditkarte funktioniert unabhängig von Ihrem SCHUFA-Score, weil Sie ausschließlich vorhandenes Guthaben ausgeben.",
      },
      {
        question: "Sind Karten ohne SCHUFA teurer?",
        answer:
          "Nicht grundsätzlich. Es gibt Prepaid-Karten ohne Jahresgebühr. Aufladeentgelte und Gebühren für Bargeldabhebungen sind hier aber häufiger als bei klassischen Kreditkarten.",
      },
    ],
  },
  {
    path: "kreditkarte-beantragen",
    pageKey: "landing-kreditkarte-beantragen",
    seoTitle: "Kreditkarte beantragen 2026 – Voraussetzungen, Ablauf & Karten",
    seoDescription:
      "Kreditkarte beantragen: Welche Voraussetzungen gelten, wie der Antrag Schritt für Schritt abläuft und welche Karten schnell verfügbar sind.",
    h1: "Kreditkarte beantragen",
    intro:
      "Voraussetzungen, Ablauf und Bearbeitungszeit im Überblick — plus die Karten, die sich online ohne großen Aufwand beantragen lassen.",
    baseFilter: () => true,
    countNoun: "Kreditkarten zur Auswahl",
    chips: [
      { id: "free", label: "Ohne Jahresgebühr", predicate: hasNoAnnualFee },
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
      { id: "prepaid", label: "Ohne SCHUFA (Prepaid)", predicate: isPrepaid },
      { id: "real-credit", label: "Echte Kreditkarte", predicate: isRealCreditCard },
      { id: "travel", label: "Mit Reiseversicherung", predicate: hasTravelInsurance },
    ],
    sections: [
      {
        heading: "Diese Voraussetzungen müssen Sie erfüllen",
        paragraphs: [
          "Für eine Kreditkarte mit Kreditrahmen prüfen Banken Wohnsitz, Alter, regelmäßiges Einkommen und Bonität. Für Prepaid-Karten entfallen Einkommensnachweis und Bonitätsprüfung meist vollständig.",
        ],
        bullets: [
          "Wohnsitz in Deutschland und deutsches Bankkonto",
          "Mindestalter 18 Jahre (Prepaid teilweise früher)",
          "Regelmäßiges Einkommen bei Karten mit Kreditrahmen",
          "Gültiger Ausweis für die Identitätsprüfung",
        ],
      },
      {
        heading: "So läuft der Antrag ab",
        paragraphs: [
          "Der komplette Ablauf ist online möglich und dauert in der Regel wenige Minuten. Die Karte selbst kommt anschließend per Post, viele Anbieter stellen vorab eine digitale Karte für Apple Pay oder Google Pay bereit.",
        ],
        bullets: [
          "Online-Antrag ausfüllen: persönliche Daten, Beschäftigung, Einkommen",
          "Identität bestätigen per Video-Ident oder Post-Ident",
          "Bonitätsprüfung und Entscheidung, oft binnen Minuten",
          "Kartenversand innerhalb weniger Werktage, PIN separat",
        ],
      },
      {
        heading: "Wenn der Antrag abgelehnt wird",
        paragraphs: [
          "Häufige Gründe sind ein zu geringes oder unregelmäßiges Einkommen, eine kurze Beschäftigungsdauer oder negative SCHUFA-Merkmale. In diesem Fall ist eine Prepaid-Karte der zuverlässigste Weg zu einer weltweit einsetzbaren Karte.",
        ],
      },
    ],
    faqs: [
      {
        question: "Wie lange dauert es, bis ich die Kreditkarte habe?",
        answer:
          "Die Entscheidung fällt bei Online-Anträgen oft innerhalb weniger Minuten. Der Versand der physischen Karte dauert anschließend meist drei bis zehn Werktage.",
      },
      {
        question: "Welche Unterlagen brauche ich?",
        answer:
          "Einen gültigen Personalausweis oder Reisepass sowie Angaben zu Beschäftigung und Einkommen. Manche Banken verlangen zusätzlich Gehaltsnachweise oder Kontoauszüge.",
      },
      {
        question: "Kann ich als Student eine Kreditkarte beantragen?",
        answer:
          "Ja. Einige Anbieter vergeben Karten ohne Mindesteinkommen. Alternativ ist eine Prepaid-Karte ohne Bonitätsprüfung möglich.",
      },
      {
        question: "Schadet ein abgelehnter Antrag meiner SCHUFA?",
        answer:
          "Eine Konditionenanfrage ist score-neutral. Mehrere Kreditanfragen in kurzer Zeit können sich dagegen negativ auswirken — stellen Sie Anträge deshalb nacheinander.",
      },
    ],
  },
  {
    path: "reisekreditkarte",
    pageKey: "landing-reisekreditkarte",
    seoTitle: "Reisekreditkarte 2026 – Karten mit Reiseversicherung im Vergleich",
    seoDescription:
      "Reisekreditkarten im Vergleich: Karten mit Auslandskrankenversicherung, Reiserücktritt und Gepäckschutz — inklusive Hinweisen zu Mietwagen und Gebühren im Ausland.",
    h1: "Reisekreditkarte: Karten mit Reiseversicherung",
    intro:
      "Karten, bei denen ein Versicherungspaket für Reisen enthalten ist. Filtern Sie nach Auslandskrankenversicherung, Reiserücktritt und Gepäckschutz — und sehen Sie sofort, welche Karte dafür eine Jahresgebühr verlangt.",
    baseFilter: hasTravelInsurance,
    countNoun: "Karten mit Reiseversicherung",
    chips: [
      { id: "health", label: "Auslandskrankenversicherung", predicate: (c) => c.travel_health_insurance === true },
      { id: "cancel", label: "Reiserücktritt", predicate: (c) => c.travel_cancellation_insurance === true },
      { id: "baggage", label: "Gepäckversicherung", predicate: (c) => c.travel_baggage_insurance === true },
      { id: "accident", label: "Unfallversicherung", predicate: (c) => c.travel_accident_insurance === true },
      { id: "free", label: "Ohne Jahresgebühr", predicate: hasNoAnnualFee },
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
      { id: "real-credit", label: "Echte Kreditkarte", predicate: isRealCreditCard },
    ],
    sections: [
      {
        heading: "Was eine Reisekreditkarte wirklich abdeckt",
        paragraphs: [
          "Der Begriff „Reiseversicherung“ steht bei Kreditkarten für ein Paket aus mehreren Einzelversicherungen. Welche davon enthalten sind, unterscheidet sich stark. Am wichtigsten ist in der Praxis die Auslandskrankenversicherung, weil eine Behandlung außerhalb der EU schnell mehrere tausend Euro kostet.",
          "Prüfen Sie vor der Reise immer die Bedingungen der Bank: Fast alle Pakete gelten nur für Reisen bis zu einer bestimmten Dauer und setzen häufig voraus, dass die Reise ganz oder teilweise mit der Karte bezahlt wurde.",
        ],
        bullets: [
          "Auslandskrankenversicherung: Behandlung und Rücktransport im Ausland",
          "Reiserücktritt: Stornokosten, wenn die Reise nicht angetreten werden kann",
          "Reisegepäck: Verlust oder Beschädigung des aufgegebenen Gepäcks",
          "Unfallversicherung: Leistungen bei Unfällen auf der Reise",
        ],
      },
      {
        heading: "Mietwagen und Hotel: echte Kreditkarte nötig",
        paragraphs: [
          "Autovermietungen und viele Hotels blockieren eine Kaution auf der Karte. Das funktioniert nur mit einer echten Kreditkarte, die über einen Verfügungsrahmen verfügt. Prepaid- und Debitkarten werden am Schalter regelmäßig abgelehnt, auch wenn genug Guthaben vorhanden ist.",
        ],
      },
      {
        heading: "Gebühren im Ausland",
        paragraphs: [
          "Neben der Versicherung entscheidet vor allem das Fremdwährungsentgelt darüber, wie teuer eine Karte auf Reisen wird — üblich sind 1,75 % bis 2,00 % je Zahlung außerhalb des Euroraums. Diese Gebühr führen wir derzeit noch nicht je Karte auf; Sie finden sie im Preis- und Leistungsverzeichnis des jeweiligen Anbieters. Lehnen Sie im Ausland außerdem immer die Abrechnung in Euro ab und zahlen Sie in der Landeswährung.",
        ],
      },
    ],
    faqs: [
      {
        question: "Welche Reisekreditkarte ist die beste?",
        answer:
          "Das hängt von Ihrem Reiseverhalten ab. Wer selten reist, fährt mit einer gebührenfreien Karte plus separater Auslandskrankenversicherung oft günstiger. Wer mehrmals im Jahr verreist, profitiert von einem Karten-Versicherungspaket.",
      },
      {
        question: "Gilt die Versicherung automatisch?",
        answer:
          "Nicht immer. Viele Anbieter verlangen, dass die Reise ganz oder anteilig mit der Karte bezahlt wurde. Zudem sind Reisen meist nur bis zu einer bestimmten Dauer, häufig 45 bis 90 Tage, versichert.",
      },
      {
        question: "Brauche ich für einen Mietwagen eine Kreditkarte?",
        answer:
          "In der Regel ja. Für die Kaution wird ein Verfügungsrahmen blockiert, was nur mit einer echten Kreditkarte funktioniert. Der Filter „Echte Kreditkarte“ zeigt passende Karten.",
      },
      {
        question: "Was kostet das Bezahlen in Fremdwährung?",
        answer:
          "Üblich sind 1,75 % bis 2,00 % des Umsatzes. Einige Karten verzichten vollständig darauf. Die genaue Höhe steht im Preis- und Leistungsverzeichnis der jeweiligen Bank.",
      },
    ],
  },
  {
    path: "kreditkarte-ohne-girokonto",
    pageKey: "landing-kreditkarte-ohne-girokonto",
    seoTitle: "Kreditkarte ohne Girokonto 2026 – unabhängig vom Konto beantragen",
    seoDescription:
      "Kreditkarte ohne Girokonto: Karten, die unabhängig von einem neuen Konto ausgegeben und über Ihr bestehendes Konto abgerechnet werden. Alle Konditionen im Vergleich.",
    h1: "Kreditkarte ohne Girokonto",
    intro:
      "Diese Karten erhalten Sie, ohne ein neues Girokonto eröffnen zu müssen. Die monatliche Abrechnung wird per Lastschrift von Ihrem bestehenden Konto eingezogen — Ihre Bankverbindung bleibt also, wie sie ist.",
    baseFilter: withoutAccount,
    countNoun: "Karten ohne neues Girokonto",
    chips: [
      { id: "free", label: "Ohne Jahresgebühr", predicate: hasNoAnnualFee },
      { id: "real-credit", label: "Echte Kreditkarte", predicate: isRealCreditCard },
      { id: "prepaid", label: "Prepaid", predicate: isPrepaid },
      { id: "travel", label: "Mit Reiseversicherung", predicate: hasTravelInsurance },
      { id: "visa", label: "Visa", predicate: isNetwork("visa") },
      { id: "mastercard", label: "Mastercard", predicate: isNetwork("mastercard") },
      { id: "amex", label: "American Express", predicate: isNetwork("american_express") },
    ],
    sections: [
      {
        heading: "Eigenständige Karte oder Konto-Paket?",
        paragraphs: [
          "Kreditkarten werden in Deutschland auf zwei Wegen vergeben. Entweder als eigenständige Karte, die unabhängig von Ihrer Hausbank läuft und deren Abrechnung per Lastschrift von Ihrem vorhandenen Konto eingezogen wird. Oder als Bestandteil eines Girokontos, das Sie beim Anbieter neu eröffnen.",
          "Beide Wege haben ihre Berechtigung. Wenn Sie mit Ihrer Bank zufrieden sind und nur eine Karte brauchen, ist die eigenständige Karte der einfachere Weg: kein Kontowechsel, keine Umstellung von Daueraufträgen, kein zusätzliches Konto in der Steuerübersicht.",
        ],
        bullets: [
          "Eigenständige Karte: bestehendes Konto behalten, nur Lastschriftmandat nötig",
          "Konto-Paket: oft günstigere Konditionen, aber neues Konto erforderlich",
          "Der Antrag läuft in beiden Fällen vollständig online",
        ],
      },
      {
        heading: "Worauf Sie achten sollten",
        paragraphs: [
          "Auch eine Karte ohne Kontopflicht setzt eine Bonitätsprüfung voraus, sofern sie einen Kreditrahmen bietet. Achten Sie außerdem auf den Abrechnungsmodus: Bei Teilzahlung wird der offene Betrag verzinst, bei Vollzahlung nicht. Stellen Sie die Karte nach Erhalt im Kundenportal auf Vollzahlung um, falls sie ab Werk anders eingestellt ist.",
        ],
      },
    ],
    faqs: [
      {
        question: "Bekomme ich eine Kreditkarte ohne neues Girokonto?",
        answer:
          "Ja. Ein großer Teil der Karten wird eigenständig ausgegeben. Die monatliche Abrechnung ziehen die Anbieter per Lastschrift von Ihrem bestehenden Girokonto ein.",
      },
      {
        question: "Muss ich mein Konto beim gleichen Anbieter haben?",
        answer:
          "Nein. Sie geben lediglich ein Lastschriftmandat für ein deutsches Konto. Bei welcher Bank dieses Konto geführt wird, spielt keine Rolle.",
      },
      {
        question: "Wird trotzdem meine Bonität geprüft?",
        answer:
          "Bei Karten mit Kreditrahmen ja, unabhängig davon, ob ein Konto dazugehört. Nur Prepaid-Karten kommen in der Regel ohne Bonitätsprüfung aus.",
      },
      {
        question: "Kann ich die Karte später kündigen?",
        answer:
          "Ja, Kreditkartenverträge sind ohne Kontobindung meist mit kurzer Frist kündbar. Gleichen Sie vorher den offenen Saldo vollständig aus.",
      },
    ],
  },
  {
    path: "kreditkarte-fuer-studenten",
    pageKey: "landing-kreditkarte-fuer-studenten",
    seoTitle: "Kreditkarte für Studenten 2026 – kostenlose Karten im Vergleich",
    seoDescription:
      "Kreditkarten für Studenten im Vergleich: ohne Jahresgebühr, teils ohne Einkommensnachweis und ohne SCHUFA-Prüfung. Alle Konditionen transparent.",
    h1: "Kreditkarte für Studenten",
    intro:
      "Als Student zählt vor allem eins: keine laufenden Kosten. Diese Karten verzichten dauerhaft auf die Jahresgebühr — filtern Sie zusätzlich nach Prepaid-Karten ohne Bonitätsprüfung und Karten ohne neues Girokonto.",
    baseFilter: hasNoAnnualFee,
    countNoun: "kostenlose Karten für Studenten",
    chips: [
      { id: "prepaid", label: "Prepaid (ohne SCHUFA)", predicate: isPrepaid },
      { id: "no-account", label: "Ohne Girokonto", predicate: withoutAccount },
      { id: "real-credit", label: "Echte Kreditkarte", predicate: isRealCreditCard },
      { id: "travel", label: "Mit Reiseversicherung", predicate: hasTravelInsurance },
      { id: "visa", label: "Visa", predicate: isNetwork("visa") },
      { id: "mastercard", label: "Mastercard", predicate: isNetwork("mastercard") },
    ],
    sections: [
      {
        heading: "Die erste eigene Kreditkarte",
        paragraphs: [
          "Für die meisten Studierenden ist die Kreditkarte der erste Schritt ins Thema Kredit. Der wichtigste Punkt dabei: Die Karte sollte dauerhaft kostenlos sein, nicht nur im ersten Jahr. Alle Karten in diesem Vergleich erfüllen genau das.",
          "Ein fester Nebenjob oder ein Einkommensnachweis ist nicht bei jeder Karte nötig. Prepaid-Karten kommen ganz ohne Einkommens- und Bonitätsprüfung aus, weil Sie nur vorhandenes Guthaben ausgeben können.",
        ],
        bullets: [
          "Dauerhaft 0 € Jahresgebühr statt Willkommensrabatt",
          "Prepaid-Karte als Option ohne SCHUFA-Prüfung",
          "Vollzahlung einstellen, um Zinsen von Anfang an zu vermeiden",
        ],
      },
      {
        heading: "SCHUFA und Bonität als Student",
        paragraphs: [
          "Auch ohne negativen Eintrag kann der SCHUFA-Score junger Menschen niedrig ausfallen, weil schlicht Daten fehlen. Das führt gelegentlich zu Ablehnungen bei Karten mit Kreditrahmen — ohne dass Sie etwas falsch gemacht hätten.",
          "In dem Fall ist eine Prepaid-Karte der sichere Weg: keine Bonitätsprüfung, weltweite Akzeptanz und volle Kostenkontrolle. Wer später eine echte Kreditkarte möchte, sammelt mit pünktlichen Abrechnungen positive Historie.",
        ],
      },
      {
        heading: "Im Auslandssemester und auf Reisen",
        paragraphs: [
          "Wer ein Semester im Ausland plant, sollte auf zwei Dinge achten: das Entgelt für Zahlungen in Fremdwährung und die Gebühr für Bargeldabhebungen außerhalb des Euroraums. Eine Karte mit Reiseversicherung kann zusätzlich sinnvoll sein — prüfen Sie, ob Studierende im Versicherungspaket eingeschlossen sind.",
        ],
      },
    ],
    faqs: [
      {
        question: "Bekomme ich als Student eine Kreditkarte ohne Einkommen?",
        answer:
          "Ja. Prepaid-Karten werden ohne Einkommensnachweis und Bonitätsprüfung ausgegeben, weil kein Kredit vergeben wird. Bei Karten mit Kreditrahmen verlangen manche Anbieter kein Mindesteinkommen, prüfen aber die Bonität.",
      },
      {
        question: "Ab welchem Alter kann ich eine Kreditkarte beantragen?",
        answer:
          "Karten mit Kreditrahmen gibt es ab 18 Jahren. Einige Prepaid-Karten erhalten bereits Jugendliche mit Zustimmung der Erziehungsberechtigten.",
      },
      {
        question: "Ist eine kostenlose Kreditkarte für Studenten wirklich kostenlos?",
        answer:
          "Bei der Jahresgebühr ja — alle Karten auf dieser Seite verzichten dauerhaft darauf. Gebühren können trotzdem für Bargeldabhebungen, Fremdwährung oder Teilzahlung anfallen. Prüfen Sie das Preis- und Leistungsverzeichnis.",
      },
      {
        question: "Debit, Prepaid oder echte Kreditkarte — was passt zu mir?",
        answer:
          "Für den Alltag und Online-Einkäufe reichen Prepaid- oder Debitkarten völlig aus. Für Mietwagen, Hotelkautionen und als finanzielle Reserve brauchen Sie eine echte Kreditkarte mit Kreditrahmen.",
      },
    ],
  },
];

export const segmentPageByPath = (path: string): SegmentPageConfig | undefined =>
  segmentPages.find((page) => page.path === path);

/**
 * "Kurzantwort" per landing page: two to three sentences that answer the search
 * query directly, so search engines and AI assistants can quote the page.
 */
export const SEGMENT_SHORT_ANSWERS: Record<string, string> = {
  "kostenlose-kreditkarte":
    "Kostenlos heißt bei fast allen Anbietern nur: keine Jahresgebühr. Tatsächlich gebührenfrei ist eine Karte erst, wenn auch das Fremdwährungsentgelt, die Bargeldgebühr und eine mögliche Girokontopflicht wegfallen. In diesem Vergleich sehen Sie diese vier Positionen je Karte einzeln statt als Werbeversprechen.",
  "prepaid-kreditkarte":
    "Eine Prepaid-Kreditkarte funktioniert auf Guthabenbasis: Sie laden Geld auf und geben nur aus, was vorhanden ist. Deshalb gibt es keinen Kreditrahmen, keine Zinsen und meist keine SCHUFA-Prüfung. Für Mietwagen- und Hotelkautionen wird sie allerdings häufig nicht akzeptiert.",
  "kreditkarte-ohne-schufa":
    "Eine echte Kreditkarte mit Kreditrahmen gibt es in Deutschland praktisch nicht ohne Bonitätsprüfung. Ohne SCHUFA-Abfrage erhalten Sie vor allem Prepaid-Karten auf Guthabenbasis und teilweise Debitkarten. Seriöse Anbieter verlangen niemals eine Vorabgebühr für eine Zusage.",
  "kreditkarte-beantragen":
    "Der Antrag läuft vollständig online: Formular ausfüllen, Identität per Video- oder Post-Ident bestätigen, Entscheidung oft binnen Minuten. Für Karten mit Kreditrahmen brauchen Sie Wohnsitz in Deutschland, ein Mindestalter von 18 Jahren, regelmäßiges Einkommen und eine ausreichende Bonität. Die physische Karte kommt meist innerhalb von drei bis zehn Werktagen.",
  reisekreditkarte:
    "Eine Reisekreditkarte lohnt sich vor allem wegen zweier Punkte: einem niedrigen oder entfallenden Fremdwährungsentgelt und einem enthaltenen Versicherungspaket, meist mit Auslandskrankenversicherung. Für Mietwagen- und Hotelkautionen brauchen Sie zusätzlich eine echte Kreditkarte mit Verfügungsrahmen. Versicherungen gelten häufig nur, wenn die Reise mit der Karte bezahlt wurde.",
  "kreditkarte-ohne-girokonto":
    "Viele Kreditkarten werden eigenständig vergeben: Die monatliche Abrechnung wird per Lastschrift von Ihrem bestehenden Girokonto eingezogen, ein Kontowechsel ist nicht nötig. Bei welcher Bank Ihr Konto geführt wird, spielt keine Rolle. Bietet die Karte einen Kreditrahmen, wird die Bonität trotzdem geprüft.",
  "kreditkarte-fuer-studenten":
    "Für Studierende zählt vor allem, dass die Karte dauerhaft 0 € Jahresgebühr kostet — nicht nur im ersten Jahr. Ohne festes Einkommen ist eine Prepaid-Karte der zuverlässigste Weg, weil sie ohne Einkommensnachweis und Bonitätsprüfung ausgegeben wird. Für Auslandssemester sind zusätzlich Fremdwährungs- und Bargeldgebühren entscheidend.",
};
